/**
 * @fileoverview Ghost-chart empty state for dashboard cards. Renders a faded
 * decorative preview of the chart's own shape (area curve, donut ring, bar
 * rows) behind a compact icon + message overlay, so an empty card still
 * "looks like itself". The `banner` variant is a slim horizontal layout with
 * no ghost, used for full-width fallbacks.
 * @module shared/components/ui/ChartEmptyState
 *
 * @example
 * ```tsx
 * <ChartEmptyState
 *   variant="area"
 *   icon={TrendingUp}
 *   message={t('charts.profitOverTime.noDataForPeriod')}
 *   hint={t('charts.emptyHint')}
 *   className="h-chart-lg"
 * />
 * ```
 */

import { type ReactElement, type ReactNode, useId } from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

export interface ChartEmptyStateProps {
  /** Which ghost decoration to render behind the message */
  variant: 'area' | 'donut' | 'bars' | 'banner'
  /** Lucide icon shown in the small disc */
  icon: LucideIcon
  /** Primary message line (already translated) */
  message: string
  /** Muted secondary line (already translated) */
  hint?: string
  /** Optional CTA slot rendered below the text (e.g. connect buttons) */
  children?: ReactNode
  /** Height/aspect utilities from the caller, e.g. h-chart-lg, h-48 */
  className?: string
}

function AreaGhost(): ReactElement {
  // useId output can contain characters (e.g. «») that are unsafe inside a
  // url(#...) SVG reference — strip to a safe charset (digits keep it unique).
  const gradientId = `chart-empty-fade-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  return (
    <svg
      viewBox="0 0 400 160"
      preserveAspectRatio="none"
      className="h-full w-full"
      data-testid="ghost-area"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.08" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[40, 80, 120].map(y => (
        <line
          key={y}
          x1="0"
          y1={y}
          x2="400"
          y2={y}
          stroke="currentColor"
          strokeOpacity="0.06"
          strokeWidth="1"
        />
      ))}
      <path
        d="M0,125 C40,105 70,132 110,108 C150,84 180,112 220,72 C260,46 300,88 340,62 C365,50 385,56 400,42 L400,160 L0,160 Z"
        fill={`url(#${gradientId})`}
      />
      <path
        d="M0,125 C40,105 70,132 110,108 C150,84 180,112 220,72 C260,46 300,88 340,62 C365,50 385,56 400,42"
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="1.5"
        strokeDasharray="6 5"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

function DonutGhost(): ReactElement {
  return (
    <svg viewBox="0 0 200 200" className="h-full w-full" data-testid="ghost-donut">
      <circle
        cx="100"
        cy="100"
        r="62"
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.1"
        strokeWidth="16"
        strokeDasharray="120 12"
        transform="rotate(-90 100 100)"
      />
    </svg>
  )
}

const BAR_GHOST_ROWS = [
  { y: 8, width: 300 },
  { y: 34, width: 228 },
  { y: 60, width: 168 },
  { y: 86, width: 112 },
  { y: 112, width: 64 },
]

function BarsGhost(): ReactElement {
  return (
    <svg
      viewBox="0 0 400 140"
      preserveAspectRatio="none"
      className="h-full w-full p-6"
      data-testid="ghost-bars"
    >
      {BAR_GHOST_ROWS.map(row => (
        <g key={row.y} fill="currentColor">
          <rect x="0" y={row.y} width="26" height="12" rx="3" opacity="0.1" />
          <rect x="36" y={row.y} width={row.width} height="12" rx="3" opacity="0.07" />
        </g>
      ))}
    </svg>
  )
}

/**
 * Ghost-chart empty state. See module docs for usage.
 */
export function ChartEmptyState({
  variant,
  icon: Icon,
  message,
  hint,
  children,
  className,
}: ChartEmptyStateProps): ReactElement {
  if (variant === 'banner') {
    return (
      <div className={cn('flex items-center justify-center gap-3 px-6 py-5', className)}>
        <div className="flex size-8 shrink-0 items-center justify-center rounded-full border border-border/60 bg-muted/40">
          <Icon className="size-4 text-muted-foreground" />
        </div>
        <div>
          <div className="text-sm font-medium text-foreground/80">{message}</div>
          {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
        </div>
      </div>
    )
  }

  return (
    <div className={cn('relative flex flex-col items-center justify-center overflow-hidden', className)}>
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 text-muted-foreground">
        {variant === 'area' && <AreaGhost />}
        {variant === 'donut' && <DonutGhost />}
        {variant === 'bars' && <BarsGhost />}
      </div>
      <div className="relative flex flex-col items-center gap-2 px-6 text-center">
        <div className="flex size-9 items-center justify-center rounded-full border border-border/60 bg-card/80 backdrop-blur-sm">
          <Icon className="size-4 text-muted-foreground" />
        </div>
        <div className="text-sm font-medium text-foreground/80">{message}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
        {children && (
          <div className="mt-2 flex flex-col items-center gap-3 sm:flex-row">{children}</div>
        )}
      </div>
    </div>
  )
}
